import { existsSync, statSync } from 'node:fs';
import path from 'node:path';
import { getResolvedConfig } from '../config/resolved-config.js';
import type { ProjectInfo } from '../types.js';
import { backfillMissingObservationCodeRefs, type CodeRefBackfillResult } from './binder.js';
import { collectCurrentProjectFacts, type CurrentProjectFacts } from './current-facts.js';
import { indexProjectLite } from './lite-provider.js';
import {
  buildProjectContextExplain,
  buildProjectContextOverview,
  type ProjectContextExplain,
  type ProjectContextObservation,
  type ProjectContextOverview,
} from './project-context.js';
import { CodeGraphStore } from './store.js';
import {
  lensPathCandidates,
  lensVerificationHints,
  rankLensPaths,
  rankLensSources,
  resolveTaskLens,
  shouldShowLensSource,
  type TaskLens,
} from './task-lens.js';

export type AutoContextRefreshMode = 'auto' | 'always' | 'never';

export interface AutoContextRefreshResult {
  mode: AutoContextRefreshMode;
  performed: boolean;
  reason: 'forced' | 'empty-index' | 'missing-scan-time' | 'stale-index' | 'fresh-enough' | 'disabled' | 'failed';
  message: string;
  backfill?: CodeRefBackfillResult;
}

export interface AutoProjectContext {
  project: Pick<ProjectInfo, 'id' | 'name' | 'rootPath'>;
  task?: string;
  lens: TaskLens;
  currentFacts: CurrentProjectFacts;
  overview: ProjectContextOverview;
  explain: ProjectContextExplain;
  refresh: AutoContextRefreshResult;
}

export interface AutoProjectBrief {
  lens: TaskLens['id'];
  lensDescription: string;
  startHere: string[];
  reliableMemoryIds: number[];
  visibleCautionIds: number[];
  hiddenReliableCount: number;
  hiddenCautionCount: number;
  suggestedVerification: string[];
}

const DEFAULT_MAX_AGE_MS = 10 * 60 * 1000;

function activeProjectObservations(
  observations: ProjectContextObservation[],
  projectId: string,
): ProjectContextObservation[] {
  return observations.filter(obs => obs.projectId === projectId && (obs.status ?? 'active') === 'active');
}

function decideRefresh(input: {
  mode: AutoContextRefreshMode;
  status: ReturnType<CodeGraphStore['status']>;
  maxAgeMs: number;
  nowMs: number;
}): Pick<AutoContextRefreshResult, 'performed' | 'reason' | 'message'> {
  if (input.mode === 'never') {
    return { performed: false, reason: 'disabled', message: 'Automatic project scan disabled.' };
  }
  if (input.mode === 'always') {
    return { performed: true, reason: 'forced', message: 'Project scan refreshed on request.' };
  }
  if (input.status.files === 0) {
    return { performed: true, reason: 'empty-index', message: 'Project scan created because no code memory existed yet.' };
  }
  if (!input.status.indexedAt) {
    return { performed: true, reason: 'missing-scan-time', message: 'Project scan refreshed because scan time was missing.' };
  }

  const indexedAtMs = Date.parse(input.status.indexedAt);
  if (!Number.isFinite(indexedAtMs)) {
    return { performed: true, reason: 'missing-scan-time', message: 'Project scan refreshed because scan time was unreadable.' };
  }
  if (input.nowMs - indexedAtMs > input.maxAgeMs) {
    return { performed: true, reason: 'stale-index', message: 'Project scan refreshed because code memory was stale.' };
  }

  return { performed: false, reason: 'fresh-enough', message: 'Existing project scan is fresh enough.' };
}

export async function buildAutoProjectContext(input: {
  project: Pick<ProjectInfo, 'id' | 'name' | 'rootPath'>;
  dataDir: string;
  observations: ProjectContextObservation[];
  task?: string;
  refresh?: AutoContextRefreshMode;
  maxAgeMs?: number;
  now?: Date;
  exclude?: string[];
}): Promise<AutoProjectContext> {
  const refreshMode = input.refresh ?? 'auto';
  const now = input.now ?? new Date();
  const task = input.task?.trim();
  const lens = resolveTaskLens(task);
  const exclude = input.exclude ?? getResolvedConfig({ projectRoot: input.project.rootPath }).codegraph.excludePatterns;
  const store = new CodeGraphStore();
  await store.init(input.dataDir);

  const initialStatus = store.status(input.project.id);
  const decision = decideRefresh({
    mode: refreshMode,
    status: initialStatus,
    maxAgeMs: input.maxAgeMs ?? DEFAULT_MAX_AGE_MS,
    nowMs: now.getTime(),
  });

  let refresh: AutoContextRefreshResult = {
    mode: refreshMode,
    ...decision,
  };

  if (decision.performed) {
    try {
      const indexed = await indexProjectLite({
        projectId: input.project.id,
        projectRoot: input.project.rootPath,
        exclude,
      });
      store.replaceProjectIndex(input.project.id, indexed);
      const backfill = await backfillMissingObservationCodeRefs(
        store,
        activeProjectObservations(input.observations, input.project.id) as any,
      );
      refresh = { ...refresh, backfill };
    } catch (error) {
      refresh = {
        mode: refreshMode,
        performed: false,
        reason: 'failed',
        message: `Project scan failed: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }

  const overview = buildProjectContextOverview({
    project: input.project,
    store,
    observations: input.observations,
    exclude,
  });
  const explain = buildProjectContextExplain({
    project: input.project,
    store,
    observations: input.observations,
    exclude,
  });

  return {
    project: input.project,
    ...(task ? { task } : {}),
    lens,
    currentFacts: collectCurrentProjectFacts({ project: input.project, now }),
    overview,
    explain,
    refresh,
  };
}

function formatLanguages(overview: ProjectContextOverview): string {
  return overview.code.languages.length > 0
    ? overview.code.languages.map(item => `${item.language} ${item.files}`).join(', ')
    : 'none indexed yet';
}

function dedupeSourcesByObservation(
  sources: ProjectContextExplain['sources'],
): ProjectContextExplain['sources'] {
  const byObservation = new Map<number, ProjectContextExplain['sources'][number]>();
  for (const source of sources) {
    const existing = byObservation.get(source.observationId);
    if (!existing || (!existing.symbol && source.symbol)) {
      byObservation.set(source.observationId, source);
    }
  }
  return [...byObservation.values()];
}

function existingLensCandidates(rootPath: string, lens: TaskLens): string[] {
  const out: string[] = [];
  for (const candidate of lensPathCandidates(lens)) {
    const absolute = path.join(rootPath, candidate);
    try {
      if (!existsSync(absolute)) continue;
      const stat = statSync(absolute);
      if (stat.isFile()) out.push(candidate);
      if (stat.isDirectory()) out.push(candidate.replace(/\\/g, '/'));
    } catch {
      // Best-effort hints only; unreadable files should not break context.
    }
  }
  return out;
}

function rankedStartHere(context: AutoProjectContext, limit = 8): string[] {
  const candidates = [
    ...existingLensCandidates(context.project.rootPath, context.lens),
    ...context.overview.suggestedReads,
  ];
  return rankLensPaths(candidates, context.lens, context.task).slice(0, limit);
}

function lensLine(context: AutoProjectContext): string {
  return `Task lens: ${context.lens.id} - ${context.lens.description}`;
}

function lensSourceSets(context: AutoProjectContext): {
  reliableSources: ProjectContextExplain['sources'];
  cautionSources: ProjectContextExplain['sources'];
  hiddenReliableCount: number;
  hiddenCautionCount: number;
} {
  const allReliableSources = rankLensSources(
    dedupeSourcesByObservation(context.explain.sources.filter(source => source.status === 'current')),
    context.lens,
    context.task,
  );
  const reliableSources = context.lens.hideUnrelatedReliableDetails
    ? allReliableSources.filter(source => shouldShowLensSource(source, context.lens, context.task))
    : allReliableSources;
  const allCautionSources = rankLensSources(
    dedupeSourcesByObservation(context.explain.sources.filter(source => source.status !== 'current')),
    context.lens,
    context.task,
  );
  const cautionSources = context.lens.hideUnrelatedCautionDetails
    ? allCautionSources.filter(source => shouldShowLensSource(source, context.lens, context.task))
    : allCautionSources;

  return {
    reliableSources,
    cautionSources,
    hiddenReliableCount: allReliableSources.length - reliableSources.length,
    hiddenCautionCount: allCautionSources.length - cautionSources.length,
  };
}

export function buildAutoProjectBrief(context: AutoProjectContext): AutoProjectBrief {
  const { reliableSources, cautionSources, hiddenReliableCount, hiddenCautionCount } = lensSourceSets(context);
  return {
    lens: context.lens.id,
    lensDescription: context.lens.description,
    startHere: rankedStartHere(context),
    reliableMemoryIds: reliableSources
      .slice(0, context.lens.sourceLimit)
      .map(source => source.observationId),
    visibleCautionIds: cautionSources
      .slice(0, context.lens.cautionLimit)
      .map(source => source.observationId),
    hiddenReliableCount,
    hiddenCautionCount,
    suggestedVerification: lensVerificationHints(context.lens),
  };
}

function formatCurrentFactsLines(facts: CurrentProjectFacts): string[] {
  const lines = ['Current project facts'];
  if (facts.packageVersion) lines.push(`- Package version: ${facts.packageVersion}`);
  if (facts.latestChangelog) {
    lines.push(`- Latest changelog: ${facts.latestChangelog.version}${facts.latestChangelog.date ? ` (${facts.latestChangelog.date})` : ''}`);
  }

  const gitParts: string[] = [];
  if (facts.git.detached) {
    gitParts.push('detached HEAD');
  } else if (facts.git.branch) {
    gitParts.push(`branch ${facts.git.branch}`);
  }
  if (facts.git.commit) gitParts.push(`commit ${facts.git.commit}`);
  gitParts.push(facts.git.dirty ? 'dirty worktree' : 'clean worktree');
  lines.push(`- Git: ${gitParts.join(', ')}`);
  if (facts.git.latestCommit) lines.push(`- Latest commit: ${facts.git.latestCommit}`);
  lines.push('- Current facts above outrank progress/dev-log files when they conflict.');

  if (facts.staleNotes.length > 0) {
    lines.push('', 'Historical/stale project notes');
    for (const note of facts.staleNotes.slice(0, 3)) {
      const details = [
        note.lastUpdated ? `last updated ${note.lastUpdated}` : undefined,
        note.branchHint ? `branch hint ${note.branchHint}` : undefined,
        note.reason,
      ].filter(Boolean).join('; ');
      lines.push(`- ${note.path}${details ? ` (${details})` : ''}; treat as historical unless the task specifically asks for it.`);
    }
  }

  return lines;
}

export function formatAutoProjectContextSummary(context: AutoProjectContext): string {
  const reliableSources = rankLensSources(
    dedupeSourcesByObservation(context.explain.sources.filter(source => source.status === 'current')),
    context.lens,
    context.task,
  );
  const startHere = rankedStartHere(context);
  const lines = [
    `Memorix Autopilot Brief for ${context.project.name}`,
    context.task ? `Task: ${context.task}` : '',
    lensLine(context),
    '',
    ...formatCurrentFactsLines(context.currentFacts),
    '',
    `- Code memory: ${context.overview.code.files} files / ${context.overview.code.symbols} symbols / ${context.overview.code.refs} memory links`,
    `- Languages: ${formatLanguages(context.overview)}`,
    `- Memories: ${context.overview.memory.active} active / ${context.overview.memory.total} total`,
    `- Freshness: ${context.overview.freshness.current} current, ${context.overview.freshness.suspect} suspect, ${context.overview.freshness.stale} stale`,
    `- Refresh: ${context.refresh.message}`,
    '',
    'Start here',
  ].filter(Boolean);

  if (startHere.length > 0) {
    startHere.forEach((path, index) => lines.push(`${index + 1}. ${path}`));
  } else {
    lines.push('- no code-bound reads yet; inspect the task-relevant files directly');
  }

  lines.push(
    '',
    'Reliable memory',
    reliableSources.length > 0
      ? `- ${reliableSources.length} current code-bound memory link(s)`
      : '- none yet',
  );

  return lines.join('\n');
}

export function formatAutoProjectContextPrompt(context: AutoProjectContext): string {
  const lines = [
    `Memorix Autopilot Brief for ${context.project.name}`,
    context.task ? `Task: ${context.task}` : '',
    lensLine(context),
    '',
    ...formatCurrentFactsLines(context.currentFacts),
    '',
    'Project state',
    `- Code memory: ${context.overview.code.files} files, ${context.overview.code.symbols} symbols, ${context.overview.code.refs} memory links`,
    `- Languages: ${formatLanguages(context.overview)}`,
    `- Memories: ${context.overview.memory.active} active / ${context.overview.memory.total} total`,
    `- Refresh: ${context.refresh.message}`,
    '',
    'Start here',
  ].filter(Boolean);

  const startHere = rankedStartHere(context);
  if (startHere.length === 0) {
    lines.push('- no code-bound reads yet; inspect the task-relevant code directly');
  } else {
    startHere.forEach((path, index) => lines.push(`${index + 1}. ${path}`));
  }

  const { reliableSources, cautionSources, hiddenReliableCount, hiddenCautionCount } = lensSourceSets(context);

  lines.push('', 'Reliable memory');
  if (reliableSources.length === 0) {
    lines.push('- none yet');
  } else {
    for (const source of reliableSources.slice(0, context.lens.sourceLimit)) {
      const location = source.path ? `${source.path}${source.symbol ? `#${source.symbol}` : ''}` : 'missing code location';
      lines.push(`- #${source.observationId} ${source.type}: ${source.title} (${location})`);
    }
  }
  if (hiddenReliableCount > 0) {
    lines.push(`- ${hiddenReliableCount} current memory link(s) hidden because they did not match this ${context.lens.id} task.`);
  }

  lines.push('', 'Verify before trusting');
  if (cautionSources.length === 0 && context.overview.freshness.suspect === 0 && context.overview.freshness.stale === 0) {
    lines.push('- no stale or suspect memory links detected');
  } else {
    lines.push(`- ${context.overview.freshness.suspect} suspect and ${context.overview.freshness.stale} stale memory link(s); verify current code before relying on them.`);
    if (hiddenCautionCount > 0) {
      lines.push('- Only task-relevant warning details are shown.');
    }
    for (const source of cautionSources.slice(0, context.lens.cautionLimit)) {
      const location = source.path ? `${source.path}${source.symbol ? `#${source.symbol}` : ''}` : 'missing code location';
      lines.push(`- #${source.observationId} ${source.status}: ${source.title} (${location})`);
    }
  }

  lines.push(
    '',
    'Suggested verification',
    ...lensVerificationHints(context.lens).map(hint => `- ${hint}`),
    '',
    'How to use this',
    '- Treat current code-bound memory as a map, not proof.',
    '- Store durable fixes, decisions, and gotchas after the work changes the project.',
  );
  return lines.join('\n');
}
